import React, { useCallback, useEffect, useMemo } from 'react';
import { TourStep, useTour } from '@shared/components/Tour';
import { useTranslation, useHistory, useRouterHelper } from '@shared/core';
import { useGuestHotelBookingsListTelemetry } from '../GuestHotelBookingsList/GuestHotelBookingList.telemetry';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useAccommodationsTourStepContext } from './AccommodationsTourStepContext';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { Close } from '@withjoy/joykit/icons';

const ACCOMMODATIONS_TOUR_STEP_GUESTS_BADGE = 'Bookings';
const GUESTS_TOUR_STEP_IMAGE_URL = 'https://withjoy.com/assets/public/travel/accommodations-tour/guests.png';

export type GuestsTourStepProps = Readonly<{
  children: React.ReactElement;
}>;

/**
 * GuestsTourStep - Wraps TourStep with the guests-badge step name
 *
 * This component wraps content with the guests-badge tour step.
 * Includes registration logic to register the step when component mounts.
 */
export const GuestsTourStep: React.FC<GuestsTourStepProps> = ({ children }) => {
  const tour = useTour();
  const history = useHistory();
  const routerHelper = useRouterHelper();
  const telemetry = useGuestHotelBookingsListTelemetry();

  const context = useAccommodationsTourStepContext();
  const registerStep = context.registerStep;
  const tourName = context.tourName;

  // Extract tour.stop to avoid depending on entire tour object which may be recreated
  const tourStop = tour.state.stateProps.stop;

  const handleClose = useCallback(() => {
    tourStop();
  }, [tourStop]);

  const onExploreMoreHotels = useCallback(() => {
    telemetry.exploreMoreHotelsClicked(tourName, ACCOMMODATIONS_TOUR_STEP_GUESTS_BADGE);
    tourStop();
    history.push(routerHelper.buildPath(`${PlanningRoutes.accommodations}/browse`));
  }, [history, routerHelper, tourStop, telemetry, tourName]);

  const onViewPage = useCallback(() => {
    telemetry.viewPageClicked(tourName, ACCOMMODATIONS_TOUR_STEP_GUESTS_BADGE);
    tourStop();
  }, [tourStop, telemetry, tourName]);

  useEffect(() => {
    registerStep('guest', ACCOMMODATIONS_TOUR_STEP_GUESTS_BADGE);
  }, [registerStep]);

  // Memoize tooltip to prevent recreation on every render
  const tooltip = useMemo(
    () => (
      <GuestsTourStepContent
        onClose={handleClose}
        onViewPage={onViewPage}
        onExploreMoreHotels={onExploreMoreHotels}
      />
    ),
    [handleClose, onViewPage, onExploreMoreHotels],
  );

  return (
    <TourStep
      stepName={ACCOMMODATIONS_TOUR_STEP_GUESTS_BADGE}
      tourName={tourName}
      tooltip={tooltip}
    >
      {children}
    </TourStep>
  );
};

GuestsTourStep.displayName = 'GuestsTourStep';

interface GuestsTourStepContentProps
  extends Readonly<{
    onClose: () => void;
    onViewPage: () => void;
    onExploreMoreHotels: () => void;
  }> {}

/**
 * Tour step content component for the guests badge step.
 * Displays information about viewing guest bookings.
 */
const GuestsTourStepContent: React.FC<GuestsTourStepContentProps> = ({ onClose, onViewPage, onExploreMoreHotels }) => {
  const { tq } = useTranslation('websiteEditor');

  const isMobile = useIsMobileScreen();

  return (
    <Box
      position="relative"
      width={{ _: pxToRem(339), sm2: pxToRem(397) }}
      maxWidth="100%"
    >
      {/* Close button */}
      <Flex
        justifyContent="flex-end"
        position="absolute"
        top={5}
        right={4}
      >
        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={onClose}
          aria-label={tq`Close tour`}
          padding={0}
          width={pxToRem(16)}
          minWidth={pxToRem(16)}
          height={pxToRem(16)}
          data-tour-ui="true"
        >
          <Close size={16} />
        </ButtonV2>
      </Flex>
      <Box padding={7}>
        {!isMobile && (
          <Box
            marginBottom={5}
            borderRadius={12}
            overflow="hidden"
          >
            <Box
              as="img"
              src={GUESTS_TOUR_STEP_IMAGE_URL}
              alt={tq`Guests badge tour step`}
              width="100%"
              height={180}
              objectFit="cover"
            />
          </Box>
        )}

        <TextV2
          typographyVariant="hed3"
          marginBottom={5}
        >
          {tq`See where your guests are staying when booked with Joy.`}
        </TextV2>

        <Flex
          flexDirection="column"
          gap={4}
          marginBottom={6}
        >
          <TextV2
            typographyVariant="body2"
            flex={1}
          >
            {tq`If you’d like, we can notify you when a guest books, and you can view all bookings here to help with planning.`}
          </TextV2>
        </Flex>

        {/* Action buttons */}
        <Flex
          marginTop={6}
          flexDirection={{ _: 'column-reverse', sm2: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          gap={4}
        >
          <ButtonV2
            variant="ghost"
            intent="neutral"
            shape="rounded"
            width="100%"
            height={pxToRem(48)}
            onClick={onExploreMoreHotels}
            data-tour-ui="true"
          >
            {tq`Explore More Hotels`}
          </ButtonV2>
          <ButtonV2
            variant="solid"
            intent="newPrimary"
            shape="rounded"
            width={{ _: '100%', sm2: pxToRem(260) }}
            paddingX={0}
            height={pxToRem(48)}
            onClick={onViewPage}
            data-tour-ui="true"
          >
            {tq`View Page`}
          </ButtonV2>
        </Flex>
      </Box>
    </Box>
  );
};
